/**
 * Vital sign and AACI acknowledgement helpers. Pure functions, no imports,
 * so tests/unit can load this file with node --test.
 * See docs/superpowers/specs/2026-10-07-aaci-charting-design.md (backend repo).
 */

export const APPT_STATUS_CHECK_IN = 5;

export const FIELDS = [
  "systolic",
  "diastolic",
  "pulse",
  "tempC",
  "spo2",
  "weightKg",
  "heightCm",
];

/** Accepted range per field, inclusive. Must match the backend's vitalRules.js. */
export const RANGES = Object.freeze({
  systolic: [50, 300],
  diastolic: [30, 200],
  pulse: [20, 250],
  tempC: [30, 45],
  spo2: [50, 100],
  weightKg: [1, 400],
  heightCm: [30, 250],
});

export const FIELD_LABELS = Object.freeze({
  systolic: { name: "Systolic (ตัวบน)", unit: "mmHg" },
  diastolic: { name: "Diastolic (ตัวล่าง)", unit: "mmHg" },
  pulse: { name: "Pulse", unit: "bpm" },
  tempC: { name: "Temp", unit: "°C" },
  spo2: { name: "SpO₂", unit: "%" },
  weightKg: { name: "น้ำหนัก", unit: "kg" },
  heightCm: { name: "ส่วนสูง", unit: "cm" },
});

export const BP_WARNING = { systolic: 140, diastolic: 90 };
export const BP_DANGER = { systolic: 180, diastolic: 110 };

export function emptyReading() {
  return {
    systolic: null,
    diastolic: null,
    pulse: null,
    tempC: null,
    spo2: null,
    weightKg: null,
    heightCm: null,
  };
}

export function isBlank(v) {
  return v === null || v === undefined || v === "";
}

export function computeBmi(weightKg, heightCm) {
  if (isBlank(weightKg) || isBlank(heightCm)) return null;
  const m = Number(heightCm) / 100;
  if (!(m > 0)) return null;
  return Math.round((Number(weightKg) / (m * m)) * 10) / 10;
}

export function bmiCategory(bmi) {
  if (bmi === null || bmi === undefined) return null;
  if (bmi < 18.5) return { key: "under", label: "ต่ำกว่าเกณฑ์" };
  if (bmi < 23) return { key: "normal", label: "ปกติ" };
  if (bmi < 25) return { key: "over", label: "น้ำหนักเกิน" };
  return { key: "obese", label: "อ้วน" };
}

export function bpLevel(systolic, diastolic) {
  if (isBlank(systolic) || isBlank(diastolic)) return null;
  const s = Number(systolic);
  const d = Number(diastolic);
  if (s >= BP_DANGER.systolic || d >= BP_DANGER.diastolic) return "danger";
  if (s >= BP_WARNING.systolic || d >= BP_WARNING.diastolic) return "warning";
  return "normal";
}

export function isOutOfRange(field, value) {
  if (isBlank(value)) return false;
  const n = Number(value);
  const [min, max] = RANGES[field];
  return !Number.isFinite(n) || n < min || n > max;
}

export function rangeMessage(field) {
  const [min, max] = RANGES[field];
  const { name, unit } = FIELD_LABELS[field];
  return `${name} ต้องอยู่ระหว่าง ${min}–${max} ${unit}`;
}

export function hasAnyValue(reading) {
  return FIELDS.some(f => !isBlank(reading && reading[f]));
}

export function toPayload(rows) {
  return (rows || [])
    .map(r => {
      const out = {};
      FIELDS.forEach(f => {
        out[f] = isBlank(r[f]) ? null : Number(r[f]);
      });
      return out;
    })
    .filter(hasAnyValue);
}

export function validateReadings(readings) {
  for (const r of readings || []) {
    if (isBlank(r.systolic) !== isBlank(r.diastolic)) {
      return "กรุณากรอกความดันให้ครบทั้ง 2 ค่า (Systolic และ Diastolic)";
    }
    const bad = FIELDS.find(f => isOutOfRange(f, r[f]));
    if (bad) return rangeMessage(bad);
    if (!isBlank(r.systolic) && Number(r.systolic) <= Number(r.diastolic)) {
      return "Systolic (ตัวบน) ต้องมากกว่า Diastolic (ตัวล่าง)";
    }
  }
  return null;
}

/**
 * What a vitals form would save right now: the filled-in readings and the
 * validation error, if any. No readings means nothing is pending.
 */
export function pendingReadings(rows) {
  const readings = toPayload(rows);
  return {
    readings,
    error: readings.length ? validateReadings(readings) : null,
  };
}

function latestValue(readings, field) {
  for (let i = readings.length - 1; i >= 0; i--) {
    if (!isBlank(readings[i][field])) return Number(readings[i][field]);
  }
  return null;
}

export function summarizeVitals(readings) {
  if (!readings || readings.length === 0) return null;
  const bps = readings.filter(r => !isBlank(r.systolic) && !isBlank(r.diastolic));
  const last = bps.length ? bps[bps.length - 1] : null;
  const first = bps.length > 1 ? bps[0] : null;
  const weightKg = latestValue(readings, "weightKg");
  const heightCm = latestValue(readings, "heightCm");
  const firstRow = readings[0];
  return {
    bp: last
      ? {
          systolic: Number(last.systolic),
          diastolic: Number(last.diastolic),
          level: bpLevel(last.systolic, last.diastolic),
        }
      : null,
    firstBp: first
      ? { systolic: Number(first.systolic), diastolic: Number(first.diastolic) }
      : null,
    pulse: latestValue(readings, "pulse"),
    tempC: latestValue(readings, "tempC"),
    spo2: latestValue(readings, "spo2"),
    weightKg,
    heightCm,
    bmi: computeBmi(weightKg, heightCm),
    measuredDt: firstRow.measuredDt || null,
    measuredBy: firstRow.measuredU
      ? `${firstRow.measuredU.firstNameTh} ${firstRow.measuredU.lastNameTh}`
      : null,
  };
}

export function ackState(props) {
  if (!props || Number(props.statusId) !== APPT_STATUS_CHECK_IN) {
    return "notRequired";
  }
  return props.ackDt ? "acked" : "pending";
}

export function queueBadge(props) {
  const state = ackState(props);
  if (state === "acked") return { key: "acked", text: "" };
  if (state === "notRequired") return { key: "notRequired", text: "" };
  const hasVitals =
    props && Array.isArray(props.vitalSigns) && props.vitalSigns.length > 0;
  return hasVitals
    ? { key: "pending", text: "รอรับทราบ" }
    : { key: "noVitals", text: "ยังไม่วัด" };
}

/**
 * Websocket appointment updates are reloaded without vitals or the
 * acknowledging doctor, so keep what the dashboard already has when the
 * incoming payload leaves them undefined.
 */
export function mergeAaciProps(prev, next) {
  const out = { ...next };
  if (out.vitalSigns === undefined && prev) out.vitalSigns = prev.vitalSigns;
  if (out.ackDoctorName === undefined && prev) {
    out.ackDoctorName = prev.ackDoctorName;
  }
  if (out.healthReviewName === undefined && prev) {
    out.healthReviewName = prev.healthReviewName;
  }
  return out;
}
